import authStorage from "src/utils/authStorage";
/**
 * AttendantChannel Module
 * Handles connection to the AttendantChannel for attendant communication
 */
class AttendantChannel {
  /**
   * Create a new AttendantChannel module
   * @param {Object} coreService - The core WebSocket service
   * @param {Object} connectionMonitor - The connection monitor
   */
  constructor(coreService, connectionMonitor) {
    this.core = coreService;
    this.monitor = connectionMonitor;
    this.attendantSubscription = null;
    this.handlers = {};
    this._reconnectCount = 0;
    this._lastConnectAttempt = 0;
  }

  /**
   * Connect to the AttendantChannel
   * @param {string} partnerId - The partner ID
   * @param {string} attendantId - The attendant ID
   * @param {string} token - The authentication token
   * @param {Object} handlers - Event handlers for the attendant channel
   * @returns {Object|null} The subscription object or null if connection failed
   */
  connect(partnerId, attendantId, token, handlers = {}) {
    if (!this.core.initialized) {
      this.core.initialize();
    }
    
    // Track connection attempts to prevent loops
    const now = Date.now();
    const timeSinceLastConnect = now - this._lastConnectAttempt;
    this._lastConnectAttempt = now;
    
    // Prevent rapid reconnection cycles
    if (timeSinceLastConnect < 2000) {
      this._reconnectCount = (this._reconnectCount || 0) + 1;
      
      if (this._reconnectCount > 3) {
        console.warn('WEBSOCKET WARN: Too many reconnection attempts in short period, delaying', 
          this._reconnectCount, timeSinceLastConnect);
        setTimeout(() => {
          this._reconnectCount = 0;
          this.connect(partnerId, attendantId, token, handlers);
        }, 5000);
        return null;
      }
    } else {
      // Reset counter if it's been a while
      this._reconnectCount = 0;
    }
    
    // Disconnect any existing attendant subscription
    this.disconnect();
    
    // Validate required params
    if (!partnerId || !attendantId) {
      console.error('Partner ID and Attendant ID are required to connect attendant');
      return null;
    }
    
    // Store handlers
    this.handlers = handlers || {};
    
    console.log(`Connecting attendant WebSocket for partner: ${partnerId}, attendant: ${attendantId}`);
    
    // Try to get authentication token from localStorage if not provided
    let authToken = token;
    
    if (!authToken) {
      try {
        // Try various possible token names from local storage
        authToken = authStorage.getItem('@sacgpt:token') ||
                    localStorage.getItem('partner_token') ||
                    localStorage.getItem('attendant_token') ||
                    localStorage.getItem('auth_token') ||
                    localStorage.getItem('token');
        
        if (authToken) {
          console.log('WEBSOCKET DEBUG: Using token from localStorage');
        } else if (window.location.hostname === 'localhost') {
          // In development, we can work without a token
          console.info('WEBSOCKET INFO: Development environment detected - authentication token not required');
          console.info('WEBSOCKET INFO: For production, you would need to set a token in localStorage');
        } else {
          // Only show warning in production
          console.warn('WEBSOCKET WARN: No token provided or found in localStorage');
        }
      } catch (e) {
        console.warn('WEBSOCKET WARN: Unable to access localStorage for token');
      }
    }
    
    // Create subscription with proper parameters
    const params = {
      channel: 'AttendantChannel',
      partner_id: String(partnerId),
      attendant_id: String(attendantId)
    };
    
    // Only add token if it exists to avoid undefined values
    if (authToken) {
      params.token = authToken;
    } else if (window.location.hostname === 'localhost') {
      // In development, we allow connections without tokens for testing
      console.info("WEBSOCKET INFO: Connecting without authentication token in development environment");
    }
    
    // Remove explicit debug token display from logs for security
    const logParams = {...params};
    if (logParams.token) {
      logParams.token = `${logParams.token.substring(0, 6)}...`;
    }
    console.log('WEBSOCKET DEBUG: Connecting with params:', JSON.stringify(logParams));
    
    try {
      this.attendantSubscription = this.core.cable.subscriptions.create(params, {
        connected: () => {
          console.log(`Attendant WebSocket connected for partner: ${partnerId}, attendant: ${attendantId}`);
          
          // Store connection parameters for reconnection
          this.monitor.storeAttendantParams(partnerId, attendantId, authToken);
          
          // Reset reconnect counters on successful connection
          this._reconnectCount = 0;
          
          if (this.handlers.onConnected) {
            this.handlers.onConnected();
          }
        },
        
        disconnected: () => {
          this.monitor.handleDisconnection(partnerId, attendantId);
          
          if (this.handlers.onDisconnected) {
            this.handlers.onDisconnected();
          }
        },
        
        received: (data) => {
          console.log(`Attendant received message:`, data);
          
          this.handleAttendantMessage(data);
        }
      });
      
      // Add custom methods to the subscription
      this.attendantSubscription.acceptTakeover = this.createAcceptTakeoverMethod(this.attendantSubscription);
      this.attendantSubscription.sendMessage = this.createSendMessageMethod(this.attendantSubscription);
      
      return this.attendantSubscription;
    } catch (error) {
      console.error("WEBSOCKET ERROR: Failed to create subscription:", error);
      return null;
    }
  }

  /**
   * Disconnect from the AttendantChannel
   */
  disconnect() {
    if (this.attendantSubscription) {
      try {
        this.core.cable.subscriptions.remove(this.attendantSubscription);
      } catch (error) {
        console.warn('Error disconnecting from AttendantChannel:', error);
      }
      this.attendantSubscription = null;
    }
  }

  /**
   * Handle incoming attendant messages
   * @param {Object} data - The message data
   */
  handleAttendantMessage(data) {
    if (!data || !data.action) {
      console.warn('Received attendant message without action:', data);
      return;
    }

    switch (data.action) {
      case 'new_takeover_request':
        if (this.handlers.onNewTakeoverRequest) {
          this.handlers.onNewTakeoverRequest(data.takeover_id, data);
        }
        break;

      case 'takeover_accepted':
        if (this.handlers.onTakeoverAccepted) {
          this.handlers.onTakeoverAccepted(data);
        }
        break;

      case 'takeover_completed':
        if (this.handlers.onTakeoverCompleted) {
          this.handlers.onTakeoverCompleted(data);
        }
        break;

      case 'takeover_rejected':
        if (this.handlers.onTakeoverRejected) {
          this.handlers.onTakeoverRejected(data);
        }
        break;

      case 'takeover_returned_to_bot':
        if (this.handlers.onTakeoverReturnedToBot) {
          this.handlers.onTakeoverReturnedToBot(data);
        }
        break;

      case 'takeover_timeout_returned':
        if (this.handlers.onTakeoverTimeoutReturned) {
          this.handlers.onTakeoverTimeoutReturned(data);
        }
        break;

      case 'new_client_message':
        if (this.handlers.onNewClientMessage) {
          this.handlers.onNewClientMessage(data);
        }
        break;

      case 'message_sent':
        if (this.handlers.onMessageSent) {
          this.handlers.onMessageSent(data.success, data.message_id);
        }
        break;

      // Novos eventos para chats do bot
      case 'new_bot_chat':
      case 'chat_started':
        if (this.handlers.onNewBotChat) {
          this.handlers.onNewBotChat(data);
        }
        break;

      case 'chat_updated':
      case 'chats_updated':
        if (this.handlers.onChatsUpdated) {
          this.handlers.onChatsUpdated(data);
        }
        break;

      case 'chat_status_changed':
        if (this.handlers.onChatStatusChanged) {
          this.handlers.onChatStatusChanged(data);
        }
        break;

      case 'typing_indicator':
        if (this.handlers.onTypingIndicator) {
          this.handlers.onTypingIndicator(data);
        }
        break;

      case 'window_expiring_alert':
        // Instagram 24h window expiring alert
        if (this.handlers.onWindowExpiringAlert) {
          this.handlers.onWindowExpiringAlert(data);
        }
        break;

      case 'new_message':
        // New message from client during active takeover
        if (this.handlers.onNewMessage) {
          this.handlers.onNewMessage(data);
        }
        break;

      default:
        console.log(`Unhandled attendant message action: ${data.action}`, data);
        if (this.handlers.onUnhandledMessage) {
          this.handlers.onUnhandledMessage(data);
        }
    }
  }

  /**
   * Create a method for accepting takeovers
   * @param {Object} subscription - The subscription object
   * @returns {Function} The accept takeover method
   */
  createAcceptTakeoverMethod(subscription) {
    return function(takeoverId, attendantId) {
      if (!takeoverId || !attendantId) {
        console.error("WEBSOCKET ERROR: Invalid takeover or attendant ID", takeoverId, attendantId);
        return Promise.reject(new Error("Missing required parameters: takeover_id and attendant_id are required"));
      }
      
      console.log("WEBSOCKET DEBUG: Accepting takeover via WebSocket", takeoverId, attendantId);
      
      return new Promise((resolve, reject) => {
        try {
          // Store the original handler safely
          const originalReceived = subscription.received;
          
          // Create a temporary handler for this specific operation
          const tempHandler = (data) => {
            console.log("WEBSOCKET DEBUG: Got response for acceptTakeover:", data);
            
            // Check if this is a response to our accept_takeover request
            if ((data.action === 'takeover_accepted' && String(data.takeover_id) === String(takeoverId)) ||
                (data.success === true && String(data.takeover_id) === String(takeoverId))) {
              // Success
              console.log("WEBSOCKET DEBUG: Takeover accepted successfully");
              resolve(data);
              // Restore original handler
              subscription.received = originalReceived;
            }
            else if (data.error) {
              // Specific error
              console.error("WEBSOCKET ERROR: Failed to accept takeover:", data.error);
              reject(new Error(data.error));
              // Restore original handler
              subscription.received = originalReceived;
            }
            else {
              // Not related to our request, pass to original handler
              originalReceived(data);
            }
          };
          
          // Set our temporary handler
          subscription.received = tempHandler;
          
          // Send the request with validated parameters
          subscription.perform('accept_takeover', {
            takeover_id: String(takeoverId),
            attendant_id: String(attendantId)
          });
          
          // Set timeout to prevent hanging
          setTimeout(() => {
            // Restore original handler
            subscription.received = originalReceived;
            reject(new Error('Timed out waiting for takeover acceptance response'));
          }, 15000);
        } catch (err) {
          console.error("WEBSOCKET ERROR: Exception during acceptTakeover:", err);
          reject(err);
        }
      });
    };
  }

  /**
   * Create a method for sending messages
   * @param {Object} subscription - The subscription object
   * @returns {Function} The send message method
   */
  createSendMessageMethod(subscription) {
    // Store reference to the AttendantChannel instance
    const self = this;
    
    return function(takeoverId, attendantId, content) {
      if (!takeoverId || !attendantId || content === undefined) {
        console.error("WEBSOCKET ERROR: Invalid parameters for sendMessage", takeoverId, attendantId, content);
        return Promise.reject(new Error("Missing required parameters: takeover_id, attendant_id, and content are required"));
      }
      
      console.log("WEBSOCKET DEBUG: Sending message via WebSocket", takeoverId, attendantId);
      
      return new Promise((resolve, reject) => {
        try {
          // Store the original handler safely
          const originalReceived = subscription.received;
          
          // Create a temporary handler for this specific operation
          const tempHandler = (data) => {
            console.log("WEBSOCKET DEBUG: Got response for sendMessage:", data);
            
            // Check if this is a response to our send_message request
            if (data.action === 'message_sent' && data.success === true) {
              // Success
              console.log("WEBSOCKET DEBUG: Message sent successfully");
              resolve(data);
              // Restore original handler
              subscription.received = originalReceived;
            }
            else if (data.error) {
              // Error
              console.error("WEBSOCKET ERROR: Failed to send message:", data.error);
              reject(new Error(data.error));
              // Restore original handler
              subscription.received = originalReceived;
            }
            else {
              // Not related to our request, pass to original handler
              originalReceived(data);
            }
          };
          
          // Set our temporary handler
          subscription.received = tempHandler;
          
          // Validate connection and subscription using the AttendantChannel instance
          self.validateConnection(subscription)
            .then(() => {
              // Send the request with validated parameters
              subscription.perform('send_message', {
                takeover_id: String(takeoverId),
                attendant_id: String(attendantId),
                content: content,
                load_all_chats: true // Always allow sending to any chat
              });
              
              // Set timeout to prevent hanging
              setTimeout(() => {
                // Restore original handler
                subscription.received = originalReceived;
                reject(new Error('Timed out waiting for message send response'));
              }, 8000);
            })
            .catch(error => {
              // Restore original handler
              subscription.received = originalReceived;
              reject(error);
            });
        } catch (error) {
          console.error("WEBSOCKET ERROR: Exception during sendMessage:", error);
          reject(error);
        }
      });
    };
  }

  /**
   * Validate the connection and subscription
   * @param {Object} subscription - The subscription object
   * @returns {Promise} A promise that resolves if the connection is valid
   */
  validateConnection(subscription) {
    return new Promise((resolve, reject) => {
      try {
        // Check if cable and connection exist and are properly initialized
        if (!this.core.cable) {
          console.error('WEBSOCKET ERROR: Cable is not initialized');
          throw new Error('Cable is not initialized');
        }
        
        if (!this.core.cable.connection) {
          console.error('WEBSOCKET ERROR: Cable connection is not initialized');
          throw new Error('Cable connection is not initialized');
        }
        
        // Check if the connection is disconnected
        if (this.core.cable.connection.disconnected) {
          console.error('WEBSOCKET ERROR: Cable connection is disconnected');
          throw new Error('Cable connection is disconnected');
        }
        
        // Check if WebSocket exists and is open
        if (!this.core.cable.connection.webSocket) {
          console.error('WEBSOCKET ERROR: WebSocket is not initialized');
          throw new Error('WebSocket is not initialized');
        }
        
        if (this.core.cable.connection.webSocket.readyState !== WebSocket.OPEN) {
          console.error(`WEBSOCKET ERROR: WebSocket is not open (state: ${this.core.cable.connection.webSocket.readyState})`);
          throw new Error('WebSocket is not open');
        }
        
        // Then check if our subscription appears to be registered with the server
        if (!subscription) {
          console.error('WEBSOCKET ERROR: Subscription is not initialized');
          throw new Error('Subscription is not initialized');
        }
        
        if (!subscription.identifier) {
          console.error('WEBSOCKET ERROR: Subscription identifier is missing');
          throw new Error('Subscription identifier is missing');
        }
        
        if (!this.core.cable.subscriptions) {
          console.error('WEBSOCKET ERROR: Cable subscriptions is not initialized');
          throw new Error('Cable subscriptions is not initialized');
        }
        
        if (!this.core.cable.subscriptions.subscriptions) {
          console.error('WEBSOCKET ERROR: subscriptions.subscriptions is undefined');
          throw new Error('subscriptions.subscriptions is undefined');
        }
        
        // Check if our subscription is in the list of active subscriptions
        const subscriptionActive = this.core.cable.subscriptions.subscriptions.some(sub => 
          sub.identifier === subscription.identifier);
        
        if (!subscriptionActive) {
          console.error('WEBSOCKET ERROR: Subscription is not active');
          throw new Error('Subscription is not active');
        }
        
        // If we got here, both connection and subscription are active
        console.log('WEBSOCKET DEBUG: Connection and subscription are active');
        
        // Add a heartbeat ping just before sending to ensure the connection is responsive
        try {
          // Use a direct raw WebSocket ping if possible
          if (this.core.cable && this.core.cable.connection && this.core.cable.connection.webSocket && 
              this.core.cable.connection.webSocket.ping) {
            this.core.cable.connection.webSocket.ping();
          }
        } catch (e) {
          console.warn('WEBSOCKET WARN: Unable to send ping before message', e);
        }
        
        resolve();
      } catch (e) {
        console.error('WEBSOCKET ERROR: Exception during connection validation:', e);
        reject(e);
      }
    });
  }

  /**
   * Accept a takeover
   * @param {string} takeoverId - The takeover ID
   * @param {string} attendantId - The attendant ID
   * @returns {Promise} A promise that resolves when the takeover is accepted
   */
  acceptTakeover(takeoverId, attendantId) {
    if (!this.attendantSubscription) {
      return Promise.reject(new Error('Not connected to AttendantChannel'));
    }
    
    return this.attendantSubscription.acceptTakeover(takeoverId, attendantId);
  }

  /**
   * Send a message
   * @param {string} takeoverId - The takeover ID
   * @param {string} attendantId - The attendant ID
   * @param {string} content - The message content
   * @returns {Promise} A promise that resolves when the message is sent
   */
  sendMessage(takeoverId, attendantId, content) {
    if (!this.attendantSubscription) {
      return Promise.reject(new Error('Not connected to AttendantChannel'));
    }
    
    return this.attendantSubscription.sendMessage(takeoverId, attendantId, content);
  }
}

export default AttendantChannel;
